import React from "react";
import { useTranslation } from "react-i18next";
import {
  Pencil, Loader2, Share2, MapPin, X as XIcon, Calendar, Sparkles, RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/result/Badge";
import DateTakenEditor from "@/components/result/DateTakenEditor";
import { stateCode } from "@/lib/usStates";

/**
 * ResultHeader — everything between the photo carousel and the info-cards
 * column: plant name + Pencil/Share, scientific italic, translating spinner,
 * location chip (or "Tag location" button), date-taken chip + inline
 * editor, badge row (native / non-native / plant-type / keystone /
 * endangered / life-cycle / confidence / needs-id / draft), the "Wrong
 * ID? Try again" button + rejected-count line, and the yellow draft
 * banner when the scan is still a saved-for-later.
 *
 * Pure presentational — the parent still owns every mutation (rename,
 * share, clear location, save date, identify draft, retry) and passes
 * them in as callbacks.
 */
export default function ResultHeader({
  scan,
  id,
  scanState,
  isNative,
  isEndangered,
  plantType,
  confLabel,
  translating,
  placeLabel,
  editingDate,
  dateDraft,
  onDateDraftChange,
  identifyingDraft,
  retrying,
  onOpenRename,
  onShare,
  sharing,
  onTagLocation,
  onClearLocation,
  onOpenDateEditor,
  onCancelDateEditor,
  onSaveDate,
  onClearDate,
  onIdentifyDraft,
  onRetryIdentify,
}) {
  const { t } = useTranslation();
  return (
    <div className="px-5 mt-5 animate-fade-up" data-testid="result-header">
      <div className="flex items-start gap-2">
        <h1 className="font-display text-4xl text-leaf-ink leading-tight flex-1 min-w-0 break-words" data-testid="plant-common-name">
          {id.common_name}
        </h1>
        <div className="flex flex-col gap-2 shrink-0 mt-1">
          <button
            type="button"
            onClick={onOpenRename}
            data-testid="rename-button"
            aria-label={t("result.editNameAria")}
            className="w-9 h-9 rounded-full bg-white border border-[#E8E5DF] text-leaf-ink flex items-center justify-center hover:bg-parchment transition"
          >
            <Pencil className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onShare}
            disabled={sharing}
            data-testid="share-button"
            aria-label={t("share.aria")}
            className="w-9 h-9 rounded-full bg-leaf hover:bg-leaf-deep disabled:opacity-60 text-white flex items-center justify-center transition"
          >
            {sharing
              ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              : <Share2 className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {id.scientific_name && (
        <p className="mt-1 italic text-leaf-mist" data-testid="plant-scientific-name">
          {id.scientific_name}
        </p>
      )}

      {translating && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-leaf-mist" data-testid="translating-indicator">
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> {t("result.translating")}
        </p>
      )}

      {/* Location + date chips */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {scan.location?.lat != null && scan.location?.lng != null ? (
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[#E6EFE9] text-leaf px-3 py-1.5 text-xs max-w-full"
            data-testid="location-chip"
          >
            <MapPin className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
            <span className="truncate" data-testid="location-label">
              {placeLabel || `${scan.location.lat.toFixed(4)}, ${scan.location.lng.toFixed(4)}`}
            </span>
            <button
              type="button"
              onClick={onClearLocation}
              aria-label={t("result.locationClearAria")}
              data-testid="location-clear"
              className="hover:text-leaf-deep shrink-0"
            >
              <XIcon className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onTagLocation}
            data-testid="tag-location-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E8E5DF] text-leaf-ink px-3 py-1.5 text-xs hover:bg-parchment disabled:opacity-60 transition"
          >
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("result.tagLocation")}
          </button>
        )}

        {/* Date-taken chip — shows manual/EXIF photo date or falls back
            to the upload date. Pencil button opens the editor. */}
        {(() => {
          const displayDate = (scan.date_taken || scan.created_at || "").slice(0, 10);
          if (!displayDate) return null;
          const isManual = !!scan.date_taken;
          return (
            <button
              type="button"
              onClick={onOpenDateEditor}
              data-testid="date-taken-chip"
              aria-label={t("result.dateTakenEditAria")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition ${
                isManual
                  ? "bg-[#E6EFE9] text-leaf hover:bg-[#D8E6DC]"
                  : "bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment"
              }`}
            >
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span data-testid="date-taken-label">
                {isManual
                  ? t("result.dateTakenChip", { date: displayDate })
                  : t("result.dateUploadedChip", { date: displayDate })}
              </span>
              <Pencil className="w-3 h-3 opacity-60" strokeWidth={1.5} aria-hidden="true" />
            </button>
          );
        })()}
      </div>

      {editingDate && (
        <DateTakenEditor
          hasSavedDate={!!scan.date_taken}
          dateDraft={dateDraft}
          onDateDraftChange={onDateDraftChange}
          onCancel={onCancelDateEditor}
          onSave={() => onSaveDate()}
          onClear={onClearDate}
        />
      )}

      {/* Badges */}
      <div className="mt-4 flex flex-wrap gap-2">
        {isNative === true && <Badge tone="leaf" testId="badge-native">{t("result.stateNative", { state: stateCode(scanState) })}</Badge>}
        {isNative === false && <Badge tone="warn" testId="badge-non-native">{t("result.nonNative", { state: stateCode(scanState) })}</Badge>}
        {plantType === "beneficial" && <Badge tone="beneficial" testId="badge-beneficial">{t("result.beneficial")}</Badge>}
        {plantType === "weed" && <Badge tone="weed" testId="badge-weed">{t("result.weed")}</Badge>}
        {plantType === "neutral" && <Badge tone="mute" testId="badge-neutral">{t("result.neutral")}</Badge>}
        {id.is_keystone && <Badge tone="leaf" testId="badge-keystone">{t("result.keystone")}</Badge>}
        {isEndangered && <Badge tone="danger" testId="badge-endangered">{t("result.endangered", { state: scanState })}</Badge>}
        {id.life_cycle && ["annual","biennial","perennial","unknown"].includes(id.life_cycle) && (
          <Badge tone="mute" testId="badge-life-cycle">{t(`result.lifeCycle.${id.life_cycle}`)}</Badge>
        )}
        {id.confidence && !scan.is_draft && <Badge tone="mute" testId="badge-confidence">{t("result.confidence", { level: confLabel })}</Badge>}
        {scan.needs_revisit && !scan.is_draft && <Badge tone="warn" testId="badge-needs-id">{t("result.needsId")}</Badge>}
        {scan.is_draft && <Badge tone="warn" testId="badge-draft">{t("result.draftBadge")}</Badge>}
      </div>

      {/* Saved-for-later draft → big "Identify now" CTA */}
      {scan.is_draft && (
        <div className="mt-4 bg-[#FFF4DC] border border-[#E9C46A]/40 rounded-3xl p-4" data-testid="draft-banner">
          <p className="text-sm text-leaf-ink font-medium">{t("result.draftTitle")}</p>
          <p className="mt-1 text-[12px] text-leaf-mist">{t("result.draftDesc")}</p>
          <button
            type="button"
            onClick={onIdentifyDraft}
            disabled={identifyingDraft}
            data-testid="identify-draft-button"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-leaf hover:bg-leaf-deep disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 shadow-botanical transition active:scale-[0.98]"
          >
            {identifyingDraft
              ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            {identifyingDraft ? t("result.draftIdentifying") : t("result.draftIdentifyNow")}
          </button>
        </div>
      )}

      {/* Wrong ID? Try again */}
      <div className="mt-3">
        <button
          type="button"
          onClick={onRetryIdentify}
          disabled={retrying}
          data-testid="retry-identify-button"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E5DF] bg-white text-leaf-ink hover:bg-parchment disabled:opacity-60 transition px-3 py-1.5 text-xs"
        >
          {retrying
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
            : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {retrying ? t("result.retryRunning") : t("result.retryIdentify")}
        </button>
        {(scan.rejected_identifications || []).length > 0 && (
          <p className="mt-1.5 text-[11px] text-leaf-mist" data-testid="rejected-count">
            {t("result.retryRejectedCount", { n: scan.rejected_identifications.length })}
          </p>
        )}
      </div>
    </div>
  );
}
